/** Booking record — the immediate actions of the header's Actions menu and the
 *  Integration tab's reads (plan §3, §4.1). A party member's request addresses its LEAD:
 *  the feed sends the lead only (`booking_id` vs `requested_booking_id`).
 *
 *  - `POST /bookings/{pk}/restech_refresh/` — re-import the booking from its reservation
 *    system now, as a background task (202 + `task_id`). 400 `disabled` (kill switch) or
 *    `refresh_unsupported` (`reason`, `suggested_action: reimport_xml`); 429 `cooldown`
 *    (`retry_after_s`). All three reject as `BookingActionError`.
 *  - `GET /bookings/{pk}/restech_raw/` — one live, audited read of the booking at its
 *    reservation system (10 s, nothing imported) beside the newest stored webhook that
 *    touched it. Vendor failures come back IN the 200 (`live.ok: false` + `error`). Never
 *    fetched on its own: only when the operator asks (every read is audited, throttled
 *    20 a minute).
 *  - `GET /bookings/{pk}/feed/` — what the feed last sent: the feed fingerprint decoded
 *    (backfilled on read when the import recorded none) and the latest feed run's stored
 *    XML sliced to the booking. Not audited.
 *  - `GET /bookings/{pk}/vcache/` — the mobile version-cache records the app serves the
 *    booking from, with the publish verdict computed now; `POST …/vcache/refresh/`
 *    rebuilds them (or TOMBSTONES tour_participant when the app must not show it — the
 *    verdict decides, so the console shows it first).
 *  - `POST …/checkin_manifest/rebuild/` · `…/tag_context/rebuild/` (synchronous) and
 *    `…/chat/resync/` (queued, Twilio) — the cache rebuilds.
 *  - `POST …/invite/` — the trip invitation again (guest-visible), `emit_event`
 *    default true.
 *  - `POST …/links/{kind}/reveal/` — one masked guest link (bookingmgmt | review), a
 *    login credential: audited per reveal, `console_session_reveal`.
 *  - `GET …/timeline/?from&to` — the time axis: the anchors, imports, changes, audit
 *    rows, deliveries and (TBEL v2 tenants) planned notifications of the booking and its
 *    party, oldest first, each source with how far back it is complete (`covered_from`:
 *    page back with `to=covered_from`). Deliveries ≤ 31 days a request.
 *
 *  Every refusal with a coded body (400 / 429) rejects as `BookingActionError`.
 *
 *  Hand-authored (console convention); mirrors `BookingRestech*` / `BookingFeed*` /
 *  `BookingAction*` in console.openapi.yaml (contract test: bookingActions.contract.test.ts). */
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import type { ApiRequestListItem } from './apiRequests';
import {
  bookingRecordKeys,
  type BookingRecordCheckinStatus,
  type BookingRestechUnsupportedReason,
} from './bookingRecord';
import { apiGet, apiPost, ConsoleApiError } from './client';

// ---------------------------------------------------------------------------
// Refresh from the restech
// ---------------------------------------------------------------------------

export interface BookingActionWarning {
  /** `frozen` — checked in locally: only a cancellation or a no-show still applies (D8). */
  code: 'frozen';
  message: string;
}

/** 202 of POST /bookings/{pk}/restech_refresh/. */
export interface BookingRestechRefreshAccepted {
  /** Poll /bgtasks/{task_id}/; its result is a BookingRestechRefreshResult. */
  task_id: string;
  queued: boolean;
  /** The booking refreshed: the opened one, or its lead for a party member. */
  booking_id: number;
  requested_booking_id: number;
  integration: string;
  integration_display: string;
  account: string | null;
  /** The id the reservation system is asked for (a Rezdy order number, …). */
  vendor_booking_id: string;
  /** Another refresh of this booking is refused for this long. */
  cooldown_s: number;
  warnings: BookingActionWarning[];
}

export interface BookingRestechRefreshState {
  booking_state: number;
  checkin_status: BookingRecordCheckinStatus;
}

/** `/bgtasks/{task_id}/result/` of a refresh task. */
export interface BookingRestechRefreshResult {
  integration: string;
  booking_id: number;
  requested_booking_id: number | null;
  restech_account: string | null;
  vendor_booking_id: string | null;
  outcome: 'refreshed' | 'not_supported' | 'no_booking_id' | 'not_found' | 'vendor_error' | 'exception' | 'no_bookings';
  detail: string | null;
  frozen: boolean;
  matched: boolean;
  bookings: { vendor_booking_id: string; status: string | null; processed: boolean }[];
  duration_ms: number | null;
  state_before: BookingRestechRefreshState;
  state_after: BookingRestechRefreshState | null;
  import_run_ids: string[];
}

export type BookingActionErrorCode =
  | 'disabled'
  | 'refresh_unsupported'
  | 'cooldown'
  | 'invalid_request'
  | 'no_guest'
  | 'not_publishable'
  | 'no_invite_link'
  | 'no_departure'
  | 'link_unavailable'
  | 'invalid_window';

/** The 400 / 429 body of an immediate booking action. */
export interface BookingActionErrorBody {
  detail: string;
  code: BookingActionErrorCode;
  reason?: BookingRestechUnsupportedReason | null;
  suggested_action?: 'reimport_xml' | null;
  retry_after_s?: number | null;
}

/** A refused immediate action (a coded 400, or 429 `cooldown`). */
export class BookingActionError extends ConsoleApiError {
  constructor(
    status: number,
    public readonly action: BookingActionErrorBody,
    message: string,
  ) {
    super(status, action, message);
  }
}

const ACTION_ERROR_CODES: ReadonlySet<string> = new Set<BookingActionErrorCode>([
  'disabled',
  'refresh_unsupported',
  'cooldown',
  'invalid_request',
  'no_guest',
  'not_publishable',
  'no_invite_link',
  'no_departure',
  'link_unavailable',
  'invalid_window',
]);

/** A ConsoleApiError carrying an immediate action's coded body → BookingActionError;
 *  anything else unchanged. */
export function toBookingActionError(e: unknown): unknown {
  if (!(e instanceof ConsoleApiError) || (e.status !== 400 && e.status !== 429)) return e;
  const body = e.body as Partial<BookingActionErrorBody> | undefined;
  if (!body?.code || !ACTION_ERROR_CODES.has(body.code)) return e;
  return new BookingActionError(e.status, body as BookingActionErrorBody, e.message);
}

/** POST an immediate action; a coded refusal rejects as BookingActionError. */
async function postAction<T>(url: string, body?: unknown): Promise<T> {
  try {
    return await apiPost<T>(url, body);
  } catch (e) {
    throw toBookingActionError(e);
  }
}

export function requestRestechRefresh(id: number): Promise<BookingRestechRefreshAccepted> {
  return postAction(`/bookings/${id}/restech_refresh/`);
}

// ---------------------------------------------------------------------------
// Raw restech data
// ---------------------------------------------------------------------------

export type BookingBodyFormat = 'json' | 'xml' | 'text';

export type BookingRestechRawError =
  | BookingRestechUnsupportedReason
  | 'not_found'
  | 'vendor_error'
  | 'timeout'
  | 'exception';

/** One live read of the booking at its reservation system. `ok: false` + `error` when
 *  there is no booking to show; `body` may still hold the vendor's error answer. */
export interface BookingRestechRawFetch {
  ok: boolean;
  vendor_booking_id: string | null;
  status_code: number | null;
  content_type: string | null;
  format: BookingBodyFormat | null;
  /** Cut at 1 MiB (`truncated`); a decoded answer is re-serialised as indented JSON. */
  body: string | null;
  truncated: boolean;
  error: BookingRestechRawError | null;
  error_detail: string | null;
  /** The URL read, without its query string. */
  request_url: string | null;
  duration_ms: number | null;
}

/** The newest stored webhook / API request that touched the booking, with its body. */
export interface BookingRestechWebhook {
  request: ApiRequestListItem;
  format: BookingBodyFormat | null;
  body: string | null;
  truncated: boolean;
  /** `unavailable` — the stored body could not be read. */
  error: 'unavailable' | null;
}

/** GET /bookings/{pk}/restech_raw/. */
export interface BookingRestechRaw {
  booking_id: number;
  requested_booking_id: number;
  /** Null when no reservation system feeds it. */
  integration: string | null;
  integration_display: string | null;
  account: string | null;
  fetched_ts: string;
  live: BookingRestechRawFetch;
  last_webhook: BookingRestechWebhook | null;
}

export function fetchRestechRaw(id: number): Promise<BookingRestechRaw> {
  return apiGet(`/bookings/${id}/restech_raw/`);
}

// ---------------------------------------------------------------------------
// What the feed sent
// ---------------------------------------------------------------------------

export type BookingFingerprintStatus = 'recorded' | 'backfilled' | 'missing';

/** The booking's feed fingerprint decoded: what the feed run that recorded the current
 *  shape sent. */
export interface BookingFeedFingerprint {
  import_run_id: string;
  source_id: string | null;
  /** integration / api / url / departure / preprocessing. */
  origin: string | null;
  mode: 'full' | 'append' | null;
  recorded_ts: string;
  /** `<user>` booking attributes sent (order_ref, datetime, …). */
  booking_attrs: string[];
  /** Child elements sent (pricing, notes, tag, …). */
  booking_children: string[];
  tag_context_keys: string[];
  /** Other tag_context keys sent (the first 16, sorted). */
  tag_context_other_keys: string[];
  /** Guest attributes sent (email, first_name, …). */
  user_attrs: string[];
  permissions: { update_bookings: boolean; update_users: boolean };
}

export type BookingFeedDocumentStatus =
  | 'ok'
  | 'no_ext_id'
  | 'no_feed_run'
  | 'no_stored_xml'
  | 'too_large'
  | 'timeout'
  | 'unparseable'
  | 'not_in_document'
  | 'error';

/** The latest feed run's stored XML sliced to the booking (docslice). */
export interface BookingFeedDocument {
  status: BookingFeedDocumentStatus;
  import_run_id: string | null;
  /** Cut at 512 KiB (`truncated`). */
  xml: string | null;
  truncated: boolean;
  /** ext_ids of the other bookings kept (same order or package). */
  collateral: string[];
}

/** GET /bookings/{pk}/feed/. */
export interface BookingFeed {
  booking_id: number;
  requested_booking_id: number;
  managed_by: string | null;
  managed_by_display: string | null;
  fingerprint_status: BookingFingerprintStatus;
  /** `missing`: why (no_ext_id, no_feed_run, no_stored_xml, too_large, timeout,
   *  unparseable, unsupported_rev, not_in_document, error). */
  fingerprint_reason: string | null;
  fingerprint: BookingFeedFingerprint | null;
  /** The newest feed run that imported the booking (the document's run). */
  last_run: { import_run_id: string; origin: string | null; created_ts: string | null } | null;
  document: BookingFeedDocument;
}

export function fetchBookingFeed(id: number, signal?: AbortSignal): Promise<BookingFeed> {
  return apiGet(`/bookings/${id}/feed/`, { signal });
}

// ---------------------------------------------------------------------------
// Mobile version cache, cache rebuilds
// ---------------------------------------------------------------------------

/** Why the app does not show the booking (null when it does). */
export type BookingVcacheHiddenReason =
  | 'booking_state'
  | 'tour_hidden'
  | 'booking_hidden'
  | 'staff_visibility_window'
  | 'past_trip'
  | 'error';

/** What …/vcache/refresh/ does now: rewrite the entries, tombstone tour_participant, or
 *  nothing (a trip long over). */
export type BookingVcacheRefreshEffect = 'update' | 'unpublish' | 'none';

/** Whether the mobile app shows the booking, computed from the record as it is now —
 *  BEFORE any refresh (a refresh of an unpublished booking tombstones its entry). */
export interface BookingVcachePublish {
  /** Null when it could not be computed (reason `error`). */
  is_published: boolean | null;
  /** The trip has not ended more than a day ago. */
  is_relevant: boolean | null;
  reason: BookingVcacheHiddenReason | null;
  refresh_effect: BookingVcacheRefreshEffect;
}

/** One ClientSync record the booking is served from. */
export interface BookingVcacheEntry {
  id: number;
  /** tour_participant · tour_package · tour_template (keyed by the TOUR id) · tour · user. */
  api_type: string | null;
  spec_version: string;
  content_type: string | null;
  object_id: number | null;
  /** Shared with other bookings (the tour template, the guest's profile): a refresh of
   *  this booking does not rewrite it. */
  shared: boolean;
  version: number | null;
  timestamp: string | null;
  /** A tombstone: the app removes the object. */
  is_deleted: boolean;
  size: number;
  /** CDN copy of the full payload. */
  url: string | null;
  url_data_size: number;
  redis: { data_key: string | null; data_bytes: number; fulldata_key: string | null; fulldata_bytes: number };
  valid_from: string | null;
  valid_to: string | null;
  logid: string | null;
  user_id: number | null;
}

/** GET /bookings/{pk}/vcache/. */
export interface BookingVcache {
  booking_id: number;
  generated_ts: string;
  spec_versions: string[];
  publish: BookingVcachePublish;
  entries: BookingVcacheEntry[];
}

/** 200 of POST /bookings/{pk}/vcache/refresh/. */
export interface BookingVcacheRefreshResult {
  booking_id: number;
  refreshed_ts: string;
  /** The verdict that decided the refresh, computed before it. */
  before: BookingVcachePublish;
  effect: BookingVcacheRefreshEffect;
  /** Spec versions the refresh could not cache (0 = all). */
  uncached_versions: number;
  publish: BookingVcachePublish;
  entries: BookingVcacheEntry[];
}

/** 200 of POST /bookings/{pk}/checkin_manifest/rebuild/. */
export interface BookingCheckinManifestRebuild {
  booking_id: number;
  group_tour_id: number;
  departure_ts: string;
  /** Only while the tenant's check-in window is open does a list exist. */
  window_open: boolean;
  /** Stored now (rebuilt in the window; removed otherwise). */
  present: boolean;
  expires_in_s: number | null;
}

/** 200 of POST /bookings/{pk}/tag_context/rebuild/. */
export interface BookingTagContextRebuild {
  booking_id: number;
  /** The Redis key. */
  key: string;
  /** False once the trip is long over (the entry is removed instead). */
  cached: boolean;
  expires_in_s: number | null;
  /** The context's top-level keys (the values stay server-side). */
  keys: string[];
}

/** 200 of POST /bookings/{pk}/chat/resync/. */
export interface BookingChatResync {
  booking_id: number;
  group_tour_id: number;
  travel_date: string | null;
  /** False: a full sync of the channel was already pending (it covers this booking). */
  queued: boolean;
  /** The Celery task of the queued sync (not a background-task id). */
  task_id: string | null;
}

export function fetchBookingVcache(id: number, signal?: AbortSignal): Promise<BookingVcache> {
  return apiGet(`/bookings/${id}/vcache/`, { signal });
}

export function refreshBookingVcache(id: number): Promise<BookingVcacheRefreshResult> {
  return postAction(`/bookings/${id}/vcache/refresh/`);
}

export function rebuildCheckinManifest(id: number): Promise<BookingCheckinManifestRebuild> {
  return postAction(`/bookings/${id}/checkin_manifest/rebuild/`);
}

export function rebuildTagContext(id: number): Promise<BookingTagContextRebuild> {
  return postAction(`/bookings/${id}/tag_context/rebuild/`);
}

export function resyncChat(id: number): Promise<BookingChatResync> {
  return postAction(`/bookings/${id}/chat/resync/`);
}

// ---------------------------------------------------------------------------
// Communication, links
// ---------------------------------------------------------------------------

/** 200 of POST /bookings/{pk}/invite/. */
export interface BookingInviteResult {
  booking_id: number;
  /** `staff` — a staff booking's guide invitation; `guest` — the guest's; `none` — the
   *  guest is excluded from chat, so only the event (if emitted) went out. */
  notification: 'staff' | 'guest' | 'none';
  /** tour_participant.created; null with `emit_event: false`. `tag` is the deliveries
   *  grid's log_tag (`send_invite_link[<logid>]`). */
  event: { code: string; tag: string; logid: string | null } | null;
}

export function resendInvite(id: number, emitEvent: boolean): Promise<BookingInviteResult> {
  return postAction(`/bookings/${id}/invite/`, { emit_event: emitEvent });
}

export type BookingLinkKind = 'bookingmgmt' | 'review';

/** 200 of POST /bookings/{pk}/links/{kind}/reveal/. */
export interface BookingLinkReveal {
  booking_id: number;
  kind: BookingLinkKind;
  /** The unmasked link — a login credential. */
  url: string;
}

export function revealBookingLink(id: number, kind: BookingLinkKind): Promise<BookingLinkReveal> {
  return postAction(`/bookings/${id}/links/${kind}/reveal/`);
}

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

export type BookingTimelineKind = 'booked' | 'start' | 'end' | 'import' | 'change' | 'audit' | 'delivery' | 'planned';

/** One dot on the time axis. Exactly the key `kind` names carries its details (none for
 *  the anchors booked / start / end). */
export interface BookingTimelineItem {
  kind: BookingTimelineKind;
  ts: string;
  /** The row's id (null for the anchors). */
  id: string | null;
  /** Which booking of the party (null for a guest change or an order-scope planned row). */
  booking_id: number | null;
  label: string;
  import?: { import_run_id: string; origin: string; state: string; source_code: string | null };
  change?: {
    change_id: number;
    entity: 'booking' | 'user';
    object_id: number;
    action: string;
    source: string;
    actor_email: string | null;
    /** At most 20. */
    changed_paths: string[];
    /** The record editor's audit uuid groups one save's rows. */
    correlation_id: string | null;
    comment: string | null;
  };
  audit?: { audit_id: number; event: string; actor_email: string | null; success: boolean; task_id: string | null };
  delivery?: {
    delivery_id: string;
    event: string;
    status: string;
    log_tag: string | null;
    n_notifications: number;
    n_success: number;
    n_error: number;
  };
  planned?: {
    tbel_id: string;
    evid: string;
    event_family: string;
    /** Pending, Leased, Emitted, Skipped, Expired, Error, Completed, Cancelled, Failed. */
    state: string;
    /** Participant, User departure or Order. */
    scope: string;
    emitted_ts: string | null;
    event_log_id: string | null;
  };
}

/** One source's accounting: every item of it in `[covered_from, window.to)` is listed. */
export interface BookingTimelineSource {
  count: number;
  /** Older items exist (the item cap, the day budget, or a timeout). */
  truncated: boolean;
  covered_from: string;
  /** Its query hit the statement timeout; what it had listed stays. */
  timed_out: boolean;
}

export type BookingTimelineSourceKey = 'imports' | 'changes' | 'audit' | 'deliveries' | 'planned';

/** The tenant's TBEL v2 mode; `off` (v1) lists no planned notifications. */
export type BookingTimelinePlannedMode = 'off' | 'migration' | 'on';

/** GET /bookings/{pk}/timeline/. */
export interface BookingTimeline {
  booking_id: number;
  generated_ts: string;
  window: { from: string; to: string };
  /** The tour's zone — the axis is labelled in it. */
  timezone: string;
  /** This booking and, for a lead, its party. */
  bookings: number[];
  anchors: { booking_ts: string | null; created_ts: string | null; start_ts: string | null; end_ts: string | null };
  /** Oldest first. */
  items: BookingTimelineItem[];
  sources: {
    imports: BookingTimelineSource;
    changes: BookingTimelineSource;
    audit: BookingTimelineSource;
    /** Daily partitions read (at most 31 a request). */
    deliveries: BookingTimelineSource & { days_walked: number };
    planned: BookingTimelineSource & { mode: BookingTimelinePlannedMode };
  };
}

/** One window of the time axis; no window = the server's default (the booking's creation
 *  to a week after its end, or now when later). A 400 `invalid_window` rejects as
 *  BookingActionError. */
export async function fetchBookingTimeline(
  id: number,
  window: { from?: string; to?: string } = {},
  signal?: AbortSignal,
): Promise<BookingTimeline> {
  const params = new URLSearchParams();
  if (window.from) params.set('from', window.from);
  if (window.to) params.set('to', window.to);
  const qs = params.toString();
  try {
    return await apiGet<BookingTimeline>(`/bookings/${id}/timeline/${qs ? `?${qs}` : ''}`, { signal });
  } catch (e) {
    throw toBookingActionError(e);
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const bookingActionKeys = {
  feed: (id: number) => ['bookingRecord', 'feed', id] as const,
  raw: (id: number) => ['bookingRecord', 'restechRaw', id] as const,
  vcache: (id: number) => ['bookingRecord', 'vcache', id] as const,
  timeline: bookingRecordKeys.timeline,
};

/** The mobile cache and its publish verdict — the Cache tab, and the refresh
 *  confirmation (which shows the verdict before the operator refreshes). */
export function useBookingVcache(id: number): UseQueryResult<BookingVcache> {
  return useQuery({
    queryKey: bookingActionKeys.vcache(id),
    queryFn: ({ signal }) => fetchBookingVcache(id, signal),
  });
}

/** What the feed sent — fetched when the Integration tab opens. */
export function useBookingFeed(id: number): UseQueryResult<BookingFeed> {
  return useQuery({
    queryKey: bookingActionKeys.feed(id),
    queryFn: ({ signal }) => fetchBookingFeed(id, signal),
  });
}

/** The live raw read — never on its own: `enabled: false`, the operator's click runs
 *  `refetch()`. The answer stays until the next click (no background refetch: each read
 *  is a vendor call and an audit row). Not abortable: the server makes the vendor call
 *  and audits it either way, so a read whose last observer unmounts (a tab switch, React's
 *  strict-mode remount) still lands in the cache instead of being thrown away. */
export function useRestechRaw(id: number): UseQueryResult<BookingRestechRaw> {
  return useQuery({
    queryKey: bookingActionKeys.raw(id),
    queryFn: () => fetchRestechRaw(id),
    enabled: false,
    staleTime: Infinity,
    retry: false,
  });
}

export function useRestechRefresh(
  id: number,
): UseMutationResult<BookingRestechRefreshAccepted, unknown, void> {
  return useMutation({ mutationFn: () => requestRestechRefresh(id) });
}
